import { useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Download, RefreshCw, Sparkles, X } from "lucide-react";

import styles from "./FormTemplates.module.css";
import { getToken, useMsalAuth } from "../../authConfig";
import { fillFormTemplateApi, exportFormTemplateApi, getFormTemplateApi, listFormTemplatesApi } from "../../api/api";
import { FormFilledValues, FormTemplateDoc, FormTemplateSummary, Project } from "../../api/models";
import { useProjects } from "../../contexts/ProjectContext";
import { AnswerLoading } from "../Answer";
import { FormReviewEditor } from "./FormReviewEditor";

interface FormFillFlowProps {
    onDismiss: () => void;
}

type Stage = "loading" | "pickTemplate" | "pickProject" | "confirm" | "generating" | "review" | "error";

export const FormFillFlow = ({ onDismiss }: FormFillFlowProps) => {
    const { t } = useTranslation();
    const client = useMsalAuth ? useMsal().instance : undefined;
    const { projects, loadProjects } = useProjects();

    const [stage, setStage] = useState<Stage>("loading");
    const [templates, setTemplates] = useState<FormTemplateSummary[]>([]);
    const [template, setTemplate] = useState<FormTemplateDoc | null>(null);
    const [project, setProject] = useState<Project | null>(null);
    const [extraContext, setExtraContext] = useState("");
    const [values, setValues] = useState<FormFilledValues>({});
    const [initialValues, setInitialValues] = useState<FormFilledValues>({});
    const [approved, setApproved] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [generation, setGeneration] = useState(0);

    const bootstrapRef = useRef(false);

    const getAuthToken = useCallback(async (): Promise<string | undefined> => {
        try {
            return client ? await getToken(client) : undefined;
        } catch (authError) {
            console.error("Failed to resolve auth token:", authError);
            return undefined;
        }
    }, [client]);

    const runLoadTemplates = useCallback(async () => {
        setStage("loading");
        setErrorMessage("");
        try {
            const token = await getAuthToken();
            const response = await listFormTemplatesApi(token);
            setTemplates(response.templates);
            setStage("pickTemplate");
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("forms.error"));
            setStage("error");
        }
    }, [getAuthToken, t]);

    useEffect(() => {
        if (bootstrapRef.current) return;
        bootstrapRef.current = true;
        void runLoadTemplates();
        if (projects.length === 0) {
            loadProjects().catch(loadError => console.error("Failed to load projects:", loadError));
        }
    }, [runLoadTemplates, projects.length, loadProjects]);

    const handleTemplateSelected = useCallback(
        async (summary: FormTemplateSummary) => {
            setStage("loading");
            setErrorMessage("");
            try {
                const token = await getAuthToken();
                const fullTemplate = await getFormTemplateApi(summary.id, token);
                setTemplate(fullTemplate);
                setStage("pickProject");
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : t("forms.error"));
                setStage("error");
            }
        },
        [getAuthToken, t]
    );

    const runGenerate = useCallback(async () => {
        if (!template || !project) return;
        setStage("generating");
        setErrorMessage("");
        try {
            const token = await getAuthToken();
            const response = await fillFormTemplateApi(
                template.id,
                { project_id: project.id, extra_context: extraContext.trim() || undefined },
                token
            );
            setValues(response.filled);
            setInitialValues(response.filled);
            setApproved(false);
            setGeneration(previous => previous + 1);
            setStage("review");
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("forms.error"));
            setStage("error");
        }
    }, [template, project, extraContext, getAuthToken, t]);

    const handleRegenerate = useCallback(() => {
        if (!window.confirm(t("forms.regenerateConfirm"))) return;
        void runGenerate();
    }, [runGenerate, t]);

    const handleStartOver = useCallback(() => {
        if (!window.confirm(t("forms.startOverConfirm"))) return;
        setTemplate(null);
        setProject(null);
        setExtraContext("");
        setValues({});
        setInitialValues({});
        setApproved(false);
        setErrorMessage("");
        setStage("pickTemplate");
    }, [t]);

    const handleExport = useCallback(async () => {
        if (!template || !project || !approved) return;
        setExporting(true);
        setErrorMessage("");
        try {
            const token = await getAuthToken();
            const response = await exportFormTemplateApi(template.id, { project_id: project.id, filled: values }, token);
            const blob = await response.blob();
            const disposition = response.headers.get("Content-Disposition") || "";
            const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
            const filename = match ? decodeURIComponent(match[1]) : "formular.docx";
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : t("forms.error"));
        } finally {
            setExporting(false);
        }
    }, [template, project, approved, values, getAuthToken, t]);

    const renderStage = () => {
        if (stage === "loading" || stage === "generating") {
            return <AnswerLoading />;
        }

        if (stage === "error") {
            return (
                <div className={styles.section}>
                    <p className={styles.error}>{errorMessage || t("forms.error")}</p>
                    <div className={styles.stageActions}>
                        <button type="button" className={styles.primaryButton} onClick={() => void runLoadTemplates()}>
                            {t("forms.retry")}
                        </button>
                    </div>
                </div>
            );
        }

        if (stage === "pickTemplate") {
            return (
                <div className={styles.section}>
                    <p className={styles.hint}>{t("forms.selectTemplate")}</p>
                    {templates.length > 0 ? (
                        <div className={styles.chipRow}>
                            {templates.map(summary => (
                                <button
                                    key={summary.id}
                                    type="button"
                                    className={styles.chip}
                                    onClick={() => void handleTemplateSelected(summary)}
                                >
                                    {summary.name}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.hint}>{t("forms.noTemplates")}</p>
                    )}
                </div>
            );
        }

        if (stage === "pickProject") {
            return (
                <div className={styles.section}>
                    <div className={styles.stageActions}>
                        <button type="button" className={styles.linkButton} onClick={() => setStage("pickTemplate")}>
                            <ArrowLeft size={14} />
                            {t("forms.back")}
                        </button>
                    </div>
                    <p className={styles.hint}>{t("forms.selectProject")}</p>
                    {projects.length > 0 ? (
                        <div className={styles.chipRow}>
                            {projects.map(candidate => (
                                <button
                                    key={candidate.id}
                                    type="button"
                                    className={styles.chip}
                                    onClick={() => {
                                        setProject(candidate);
                                        setStage("confirm");
                                    }}
                                >
                                    {candidate.name}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.hint}>{t("forms.noProjects")}</p>
                    )}
                </div>
            );
        }

        if (stage === "confirm") {
            return (
                <div className={styles.section}>
                    <div className={styles.stageActions}>
                        <button type="button" className={styles.linkButton} onClick={() => setStage("pickProject")}>
                            <ArrowLeft size={14} />
                            {t("forms.back")}
                        </button>
                    </div>
                    <p className={styles.hint}>
                        {t("forms.confirmSummary", { template: template?.name ?? "", project: project?.name ?? "" })}
                    </p>
                    <div className={styles.formRow}>
                        <label className={styles.label} htmlFor="forms-extra-context">
                            {t("forms.extraContextLabel")}
                        </label>
                        <textarea
                            id="forms-extra-context"
                            className={styles.textarea}
                            value={extraContext}
                            placeholder={t("forms.extraContextPlaceholder")}
                            onChange={event => setExtraContext(event.target.value)}
                        />
                    </div>
                    <div className={styles.stageActions}>
                        <button type="button" className={styles.primaryButton} onClick={() => void runGenerate()}>
                            <Sparkles size={16} />
                            {t("forms.generate")}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.section}>
                <p className={styles.hint}>{t("forms.reviewIntro")}</p>
                {errorMessage && <p className={styles.error}>{errorMessage}</p>}
                <FormReviewEditor
                    key={generation}
                    fields={template?.schema.fields ?? []}
                    values={values}
                    initialValues={initialValues}
                    onChange={setValues}
                />
                <label className={styles.approveRow}>
                    <input type="checkbox" checked={approved} onChange={event => setApproved(event.target.checked)} />
                    {t("forms.approveLabel")}
                </label>
                <div className={styles.stageActions}>
                    <button
                        type="button"
                        className={styles.primaryButton}
                        disabled={!approved || exporting}
                        onClick={() => void handleExport()}
                    >
                        <Download size={16} />
                        {exporting ? t("forms.exporting") : t("forms.download")}
                    </button>
                    <button type="button" className={styles.linkButton} onClick={handleRegenerate}>
                        <RefreshCw size={14} />
                        {t("forms.regenerate")}
                    </button>
                    <button type="button" className={styles.linkButton} onClick={handleStartOver}>
                        <ArrowLeft size={14} />
                        {t("forms.startOver")}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.flowContainer}>
            <div className={styles.flowContent}>
                <button type="button" className={styles.dismissButton} onClick={onDismiss}>
                    <X size={18} />
                    {t("forms.close")}
                </button>
                {renderStage()}
            </div>
        </div>
    );
};
